import { type ReactNode, useContext } from "react";
import type { RowData } from "@tanstack/react-table";
import type { DataGridListCommit } from "../columns/listEditors";
import { ListCellCommitContext } from "../editing/list/listCellContext";
import { runListCommit } from "../editing/list/listEditActions";
import { DraftRowContext } from "../features/draftRows";
import { useCellControlTabIndex } from "./cellControls";
import type { DataGridCellProps } from "./types";
import styles from "./ToggleSlot.module.scss";

/** A one-click value change in a label slot, such as a task's Done toggle. */
export interface DataGridToggle<TData, TValue> {
    value: (row: TData) => TValue;
    /** The value one click saves: Done for a to-do task. */
    next: (row: TData) => TValue;
    commit: DataGridListCommit<TData, TValue>;
    /** Accessible name and tooltip: "Mark Done". */
    label: (row: TData) => string;
    /** The icon; `isPreview` draws the hover state (the next value). */
    renderIcon: (props: {
        row: TData;
        value: TValue;
        isPreview: boolean;
    }) => ReactNode;
    isDisabled?: (row: TData) => boolean;
}

interface ToggleSlotProps<
    TData extends RowData,
    TValue,
> extends DataGridCellProps<TData> {
    toggle: DataGridToggle<TData, TValue>;
}

/**
 * The toggle's button. In list tables the cell takes focus before `commit`
 * runs, so a confirmation it opens returns focus to the cell. Disabled on
 * draft rows, where the tooltip goes too.
 */
export const ToggleSlot = <TData extends RowData, TValue>({
    context,
    toggle,
}: Readonly<ToggleSlotProps<TData, TValue>>) => {
    const commitFromCell = useContext(ListCellCommitContext);
    const isDraftRow = useContext(DraftRowContext) !== null;
    const tabIndex = useCellControlTabIndex();
    const row = context.row.original;
    const value = toggle.value(row);
    const label = toggle.label(row);
    const isDisabled = isDraftRow || (toggle.isDisabled?.(row) ?? false);
    return (
        <button
            type="button"
            className={styles.toggle}
            aria-label={label}
            data-cell-tooltip={isDisabled ? undefined : label}
            data-cell-tooltip-always={isDisabled ? undefined : ""}
            data-dt-toggle=""
            tabIndex={tabIndex}
            disabled={isDisabled}
            onClick={(event) => {
                event.stopPropagation();
                const run = () =>
                    runListCommit({
                        edit: toggle,
                        row,
                        value: toggle.next(row),
                    });
                if (commitFromCell) {
                    commitFromCell(run);
                } else {
                    run();
                }
            }}
        >
            <span className={styles.icon}>
                {toggle.renderIcon({ row, value, isPreview: false })}
            </span>
            <span className={styles.preview}>
                {toggle.renderIcon({ row, value, isPreview: true })}
            </span>
        </button>
    );
};
